import DeleteDataModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import classnames from 'classnames';
import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import BackgroundImages from 'v2/common/Background/BackgroundImages';
import DataFileModalForm from 'v2/DataFiles/DataFileModalForm/DataFileModalForm';
import DataFileUploader from 'v2/DataFiles/DataFileUploader/DataFileUploader';
import { DataFileEntity } from 'v2/DataFiles/models';
import downloadIcon from 'v2/icons/download-cloud.svg';
import editIcon from 'v2/icons/edit-rectangle.svg';
import trashCanIcon from 'v2/icons/trash-outlined.svg';
import { StrapiApiError } from 'v2/models';
import { downloadFile } from 'v2/utils/fileUtils';
import restFactory from 'v2/utils/restFactory';
import DataFilesTable from '../../../DataFiles/DataFilesTable/DataFilesTable';
import DataFileDetails from '../DataFileDetails/DataFileDetails';
import styles from './providerFileLibrary.module.scss';

const ProviderFileLibrary = () => {
  const [selectedDataFile, setSelectedDataFile] =
    useState<DataFileEntity | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [itemToRemove, setItemToRemove] = useState<DataFileEntity | null>(null);
  const [dataToEdit, setDataToEdit] = useState<DataFileEntity | null>(null);
  const [showModal, setShowModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: dataFiles, isLoading } = useQuery(
    ['dataFiles'],
    async () => {
      const response = await restFactory.get<DataFileEntity[]>('data-files');
      return response;
    },
    {
      onError: (error: StrapiApiError) => {
        toast.error(`Error loading data files. ${error.message}`);
      },
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const onFileSelected = (file: File | undefined) => {
    if (file) {
      setShowModal(true);
      setSelectedFile(file);
    }
  };

  return (
    <div className={classnames('mt-5 container-xl fix-bg-images', styles.root)}>
      <BackgroundImages />
      <div>
        <h1 className="position-relative mb-4">File Library</h1>
      </div>
      <div className={classnames(styles.boxContainer, 'fix-bg-images')}>
        <div
          className={classnames(
            styles.left,
            'scroll',
            'justify-content-center'
          )}
        >
          <DataFileUploader onFileSelected={onFileSelected} />
          {!isLoading ? (
            <DataFilesTable
              className={styles.table}
              list={dataFiles || []}
              showFilesCounter={false}
              actions={(item) => (
                <div className={styles.actionContainer}>
                  <button
                    onClick={() => {
                      setDataToEdit(item);
                      setShowModal(true);
                    }}
                  >
                    <img src={editIcon} alt="editIcon"></img>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setItemToRemove(item);
                    }}
                    data-testid="deleteDataFile"
                  >
                    <img src={trashCanIcon} alt="deleteIcon"></img>
                  </button>
                  <button
                    className={styles.downloadFile}
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadFile(item.id);
                    }}
                    aria-label="downloadDataFile"
                  >
                    <img src={downloadIcon} alt="downloadFile"></img>
                  </button>
                </div>
              )}
              selectedRow={selectedDataFile}
              setSelectedRow={setSelectedDataFile}
            />
          ) : null}
        </div>
        <div className={classnames('scroll', styles.right)}>
          <DataFileDetails
            data={selectedDataFile}
            onDeselect={() => setSelectedDataFile(null)}
            onDownloadFile={downloadFile}
          />
        </div>
      </div>
      {(selectedFile || dataToEdit) && (
        <DataFileModalForm
          key={`${showModal}`}
          className={styles.addEditFileModal}
          data={dataToEdit}
          show={showModal}
          selectedFile={selectedFile}
          onHide={(refreshData) => {
            if (refreshData) {
              queryClient.invalidateQueries('dataFiles');
            }
            setShowModal(false);
            setDataToEdit(null);
            setSelectedFile(null);
          }}
          onChangeFile={onFileSelected}
        />
      )}
      <DeleteDataModal
        show={!!itemToRemove}
        onCancel={() => setItemToRemove(null)}
        onOk={async () => {
          await restFactory.delete(`data-files/${itemToRemove?.id}`);
          queryClient.invalidateQueries('dataFiles');
          setItemToRemove(null);
          if (selectedDataFile?.id === itemToRemove?.id) {
            setSelectedDataFile(null);
          }
        }}
        okBtnText="Delete"
        title="Delete File"
      >
        <>
          Are you sure you want to delete
          <strong> {itemToRemove?.name}</strong>?
        </>
      </DeleteDataModal>
    </div>
  );
};

export default ProviderFileLibrary;
